/**
 * What putting a document into a folder does.
 *
 * A placement is a MOVE, a COPY — or it is refused, and the user is told why.
 * It never changes who can reach a file: a file's visibility is fixed when it
 * is created, and nothing here (or on the server) flips it. The areas decide
 * what may move where (`vault-areas.ts`):
 *
 *   - out of Arche Saves into the user's Documents → move (the user takes it)
 *     — except an email attachment, which is COPIED (after the user agrees):
 *     it stays tied to its email, whose deletion would take a moved file
 *     with it;
 *   - within Documents                              → move;
 *   - anything in My-Fias                           → refused: it belongs to
 *     the arche that keeps it there;
 *   - into Arche Saves or My-Fias, or between two Arche Saves folders
 *                                                   → refused.
 *
 * Giving an arche access to a file is a separate, explicit action ("Give an
 * arche access…" on the file) — never what a drop means.
 *
 * This is a pure function so every way of placing a document (drop on the
 * folder tree, drop on a folder tile, the Move dialog) decides the same thing.
 * The SERVER re-decides every move and trusts none of this.
 */
import {
  EMAIL_ATTACHMENTS_PATH,
  archeNamespaceOfPath,
  isFolderPathAtOrUnder,
  vaultAreaMoveRefusal,
  vaultAreaOfPath,
  type VaultAreaMoveRefusal,
} from '@/lib/vault-areas';

export { archeNamespaceOfPath };

export interface PlacementDocument {
  folderId: string | null;
  /** The path of the folder the document is in now; `null` = the root. */
  folderPath: string | null;
}

export interface PlacementTarget {
  /** `null` = the Holder root. */
  folderId: string | null;
  path: string | null;
}

export type PlacementPlan =
  /** Already there. */
  | { kind: 'none' }
  /** A plain move between folders. */
  | { kind: 'move' }
  /** A copy into Documents instead of a move — the original stays where it
   *  is. `reason` says why a move won't do. */
  | { kind: 'copy'; reason: 'email-attachment' }
  /** Refused, for `refusal`; `archeId` names the arche whose folder is
   *  involved, when there is one, so the message can say which. */
  | {
      kind: 'blocked';
      refusal: VaultAreaMoveRefusal;
      to: 'my-fias' | 'arche-saves' | 'documents';
      archeId: string | null;
    };

export function planDocumentPlacement(
  document: PlacementDocument,
  target: PlacementTarget,
): PlacementPlan {
  if ((document.folderId ?? null) === (target.folderId ?? null)) return { kind: 'none' };
  const from = vaultAreaOfPath(document.folderPath);
  const to = vaultAreaOfPath(target.path);
  const refusal = vaultAreaMoveRefusal(from, to);
  if (refusal === null) {
    return isFolderPathAtOrUnder(document.folderPath, EMAIL_ATTACHMENTS_PATH)
      ? { kind: 'copy', reason: 'email-attachment' }
      : { kind: 'move' };
  }
  return {
    kind: 'blocked',
    refusal,
    to,
    archeId:
      refusal === 'NOT_MOVABLE'
        ? archeNamespaceOfPath(document.folderPath)
        : archeNamespaceOfPath(target.path),
  };
}
